import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/app";
import { startVkLoginRedirect } from "@/shared/api/auth";
import { hasVkEntryMarker, isVkEntryConsumed, markVkEntryConsumed, saveVkEntryReturnPath, stripVkEntryParams, takeVkEntryReturnPath, } from "@/shared/lib/vkEntry";
/**
 * Автовход через VK ID при заходе из ВК (ссылка бота с `?from=vk`).
 *
 * Флоу ПОЛНОСТРАНИЧНЫЙ, не popup: вход стартует из эффекта, вне жеста
 * пользователя, а такой `window.open` браузеры (и in-app браузер ВК) блокируют —
 * popup-вариант не сработал бы никогда. Поэтому уводим саму вкладку на ручку
 * OAuth (`startVkLoginRedirect`), предварительно запомнив целевой роут: бэк
 * после callback'а возвращает на корень сайта с `?auth=success`.
 *
 * Логика намеренно консервативная:
 *  - ждём окончания первичной проверки сессии (`isLoading`), чтобы не стартовать
 *    OAuth пользователю, который на самом деле уже авторизован;
 *  - маркер потребляем ОДИН раз и сразу убираем из URL (целевой роут и его
 *    параметры — `audioId`, utm — сохраняются), поэтому петли редиректов нет;
 *  - повторный заход по той же ссылке (маркер уже потреблён) не молчит, а
 *    открывает обычный экран входа — иначе пользователь упирается в тупик.
 */
export function useVkEntryAutoLogin(options = {}) {
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated, isLoading } = useAuth();
    const startedRef = useRef(false);
    const returnHandledRef = useRef(false);
    // Свежий колбэк в ref — иначе инлайновая функция родителя перезапускала бы эффект.
    const onManualLoginRef = useRef(options.onManualLogin);
    onManualLoginRef.current = options.onManualLogin;
    // Возврат с VK ID: бэк редиректит на корень сайта с `?auth=success|error`.
    // Целевой роут (форма релиза с нужным audioId) знаем только мы — по нему и
    // уводим. Если сохранённого роута нет, вход стартовали не отсюда — не лезем.
    useEffect(() => {
        if (returnHandledRef.current)
            return;
        if (isLoading)
            return;
        const authResult = new URLSearchParams(location.search).get("auth");
        if (!authResult)
            return;
        returnHandledRef.current = true;
        const target = takeVkEntryReturnPath();
        if (!target)
            return;
        if (authResult === "success") {
            navigate(target, { replace: true });
            return;
        }
        onManualLoginRef.current?.();
    }, [isLoading, location.search, navigate]);
    useEffect(() => {
        if (startedRef.current)
            return;
        if (isLoading)
            return;
        if (!hasVkEntryMarker(location.search))
            return;
        startedRef.current = true;
        const alreadyConsumed = isVkEntryConsumed();
        const cleanSearch = stripVkEntryParams(location.search);
        // Чистим маркер из адресной строки в любом случае (в т.ч. когда вход не
        // нужен) — ссылка из ВК не должна «залипать» в истории.
        navigate({ pathname: location.pathname, search: cleanSearch }, { replace: true });
        // Уже вошли — ничего не делаем.
        if (isAuthenticated)
            return;
        // Маркер уже отрабатывал в этой сессии, а пользователь всё ещё гость:
        // повторный редирект на OAuth дал бы петлю, поэтому показываем вход руками.
        if (alreadyConsumed) {
            onManualLoginRef.current?.();
            return;
        }
        markVkEntryConsumed();
        // Возврат с OAuth приходит на корень сайта — целевой роут запоминаем сами.
        saveVkEntryReturnPath(`${location.pathname}${cleanSearch}`);
        void startVkLoginRedirect().catch((e) => {
            console.error("VK auto login redirect failed", e);
            onManualLoginRef.current?.();
        });
    }, [
        isAuthenticated,
        isLoading,
        location.pathname,
        location.search,
        navigate,
    ]);
}
